/* ═══════════════════════════════════════════════════════════════
   zone-space — the ONE write path for whole-zone spacing (Studio
   canvas ZoneSpacer drag bars AND each palette's numeric "Spacing"
   field, e.g. EosPanel). Both call these so a value set one way
   reads back identically the other way — the contract behind the
   EOS-drag restoration (2026-08-14): drag and the numeric control
   must never disagree about what "committed" means.

   Pure, no React, no DOM — the whole point is that this stays
   testable independent of the drag gesture that calls it.
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig } from './forma';

export type ZoneSpaceRegion = 'header' | 'pre' | 'post' | 'footer' | 'eos';
export type ZoneSpaceEdge = 'before' | 'after';

/** Read the CURRENT value for one region/edge — `zoneSpace` is the
 *  canonical store; `eos`/`before` falls back to the legacy `eosSpacing` /
 *  `postBodySpacing` fields for formas saved before `zoneSpace` existed. */
export function readZoneSpace(
  forma: StandardConfig,
  region: ZoneSpaceRegion,
  edge: ZoneSpaceEdge
): number {
  const sp = forma.special || ({} as StandardConfig['special']);
  const legacy =
    region === 'eos' && edge === 'before' ? (sp.eosSpacing ?? sp.postBodySpacing ?? 24) : 0;
  return sp.zoneSpace?.[region]?.[edge] ?? legacy;
}

/** Clamp + write one region/edge's spacing (points). Returns a NEW forma —
 *  never mutates the one passed in. `eos`/`before` also mirrors the write
 *  into the legacy `eosSpacing` field so every older reader (paper.tsx's
 *  read-only `EndOfSection`, word-engine's `eosSp` fallback chain) sees the
 *  same value the new `zoneSpace` store does. */
export function writeZoneSpace(
  forma: StandardConfig,
  region: ZoneSpaceRegion,
  edge: ZoneSpaceEdge,
  value: number,
  max: number
): StandardConfig {
  const nv = Math.max(0, Math.min(max, Math.round(value)));
  const zs = { ...(forma.special?.zoneSpace || {}) };
  zs[region] = { ...(zs[region] || {}), [edge]: nv };
  const next: StandardConfig = { ...forma, special: { ...forma.special, zoneSpace: zs } };
  if (region === 'eos' && edge === 'before') {
    next.special = { ...next.special, eosSpacing: nv };
  }
  return next;
}

/** The EOS drag's ceiling: at least 30pt (the historical drag cap noted in
 *  the v2.14.32 repair — "EOS 0-30 drag cap"), but never less than the
 *  value already set — dragging must always be able to reach DOWN to the
 *  current value, even one set higher via the numeric field's own 0-300
 *  range, or the handle would be stuck unable to move. */
export function eosDragMax(current: number): number {
  return Math.max(30, current);
}
